import {state} from './State';

const shouldAlignRight = (preferredAlignment, dropdownRectangle, triggerRectangle) => {
    const willOverflowOnRight = dropdownRectangle.right > window.innerWidth;
    const hasSpaceOnLeft = triggerRectangle.right - dropdownRectangle.width >= 0;

    return (preferredAlignment === 'right' || willOverflowOnRight) && hasSpaceOnLeft;
}

const findScrollableAncestor = element => {
    let parent = element.parentElement;

    while (parent && parent !== document.body) {
        const overflowY = getComputedStyle(parent).overflowY;

        if (overflowY === 'auto' || overflowY === 'scroll') {
            return parent;
        }

        parent = parent.parentElement;
    }

    return null;
};

const getVerticalBoundary = dropdown => {
    const ancestor = findScrollableAncestor(dropdown);

    if (!ancestor) {
        return {
            bottom: window.innerHeight,
            top:    0,
        };
    }

    const rect = ancestor.getBoundingClientRect();

    return {
        bottom: Math.min(window.innerHeight, rect.bottom),
        top:    Math.max(0, rect.top),
    };
};

const shouldInvert = (preferredPosition, dropdownRectangle, triggerRectangle, boundary) => {
    const willOverflowOnBottom = dropdownRectangle.bottom > boundary.bottom;
    const hasSpaceAbove = triggerRectangle.top - dropdownRectangle.height >= boundary.top;

    return (preferredPosition === 'top' || willOverflowOnBottom) && hasSpaceAbove;
}

const resetDropdown = dropdown => {
    dropdown.classList.remove('inverted', 'right-aligned');
};

/**
 * @param {HTMLElement} dropdownContainer
 */
export const positionDropdown = (dropdownContainer) => {
    const dropdown = dropdownContainer.querySelector('.dropdown-menu');
    const trigger = dropdownContainer.querySelector('.dropdown-trigger');

    if (!dropdown || !trigger) {
        return;
    }

    resetDropdown(dropdown);

    const isFixed = getComputedStyle(dropdown).position === 'fixed';

    if (isFixed) {
        const triggerRectangle = trigger.getBoundingClientRect();

        dropdown.style.left = triggerRectangle.right + 'px';
        dropdown.style.top  = triggerRectangle.top + 'px';

        const dropdownRectangle = dropdown.getBoundingClientRect();

        if (dropdownRectangle.right > window.innerWidth) {
            dropdown.style.left = (triggerRectangle.left - dropdownRectangle.width) + 'px';
        }

        if (dropdownRectangle.bottom > window.innerHeight) {
            dropdown.style.top = (window.innerHeight - dropdownRectangle.height - 10) + 'px';
        }

        return;
    }

    const dropdownRectangle = dropdown.getBoundingClientRect();
    const triggerRectangle = trigger.getBoundingClientRect();

    const preferredAlignment = dropdownContainer.dataset.preferredAlignment;
    const preferredPosition = dropdownContainer.dataset.preferredPosition;
    const verticalBoundary = getVerticalBoundary(dropdown);

    if (shouldAlignRight(preferredAlignment, dropdownRectangle, triggerRectangle)) {
        dropdown.classList.add('right-aligned');
    }

    if (shouldInvert(preferredPosition, dropdownRectangle, triggerRectangle, verticalBoundary)) {
        dropdown.classList.add('inverted');
    }
};

const SCROLL_PADDING = 20;
const scrollAdjustments = new Map();

const restoreScrollAdjustment = dropdownContainer => {
    const adjustment = scrollAdjustments.get(dropdownContainer);

    if (!adjustment) {
        return;
    }

    adjustment.ancestor.style[adjustment.property] = adjustment.originalValue;
    scrollAdjustments.delete(dropdownContainer);
};

const sweepOrphanedAdjustments = () => {
    for (const container of scrollAdjustments.keys()) {
        if (!document.contains(container)) {
            restoreScrollAdjustment(container);
        }
    }
};

/**
 * @param {HTMLElement} dropdownContainer
 */
export const openDropdown = dropdownContainer => {
    sweepOrphanedAdjustments();
    restoreScrollAdjustment(dropdownContainer);

    dropdownContainer.classList.add('active');
    positionDropdown(dropdownContainer);
    state.hasActiveDropdown = true;

    requestAnimationFrame(() => {
        if (!dropdownContainer.classList.contains('active')) {
            return;
        }

        const dropdown = dropdownContainer.querySelector('.dropdown-menu');

        if (!dropdown) {
            return;
        }

        if (getComputedStyle(dropdown).position === 'fixed') {
            return;
        }

        const ancestor = findScrollableAncestor(dropdown);

        if (!ancestor) {
            return;
        }

        const dropdownRect = dropdown.getBoundingClientRect();
        const ancestorRect = ancestor.getBoundingClientRect();

        const overflowBelow = (dropdownRect.bottom + SCROLL_PADDING) - ancestorRect.bottom;
        const overflowAbove = (ancestorRect.top + SCROLL_PADDING) - dropdownRect.top;

        if (overflowBelow > 0) {
            const computedPadding = parseFloat(getComputedStyle(ancestor).paddingBottom) || 0;

            scrollAdjustments.set(dropdownContainer, {
                ancestor,
                property:      'paddingBottom',
                originalValue: ancestor.style.paddingBottom,
            });

            ancestor.style.paddingBottom = `${computedPadding + overflowBelow}px`;

            ancestor.scrollBy({
                top:      overflowBelow,
                behavior: 'smooth',
            });
        } else if (overflowAbove > 0) {
            ancestor.scrollBy({
                top:      -overflowAbove,
                behavior: 'smooth',
            });
        }
    });
};

/**
 * @param {HTMLElement} dropdownContainer
 */
export const closeDropdown = dropdownContainer => {
    dropdownContainer.classList.remove('active');
    const dropdown = dropdownContainer.querySelector('.dropdown-menu');
    if (dropdown) {
        resetDropdown(dropdown);
        dropdown.style.left = '';
        dropdown.style.top  = '';
    }

    restoreScrollAdjustment(dropdownContainer);

    state.hasActiveDropdown = false;
};
